import { useState } from 'react';
import { CreditCard, Lock } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { primaryButtonClass } from 'features/subscription/constants/button-styles';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { formatCredits } from 'features/usage/components/usage-credits';
import { formatNumber } from 'features/usage/utils/usage-format';
import { useTopUpPayment } from '../hooks/use-top-up-payment';
import type { CouponQuote, CreditSummary } from '../types';
import { couponBonusCredits, creditsFor, defaultPreset, presetsOrDefault, toMinor } from '../utils/amount';
import { cardLabel } from '../utils/cards';
import { AmountPicker } from './amount-picker';
import { CouponRedeem } from './coupon-redeem';
import { OpenCheckoutNotice } from './open-checkout-notice';
import { TopUpBillingDialog } from './topup-billing-dialog';
import { TopUpProgress } from './topup-progress';

interface BuyCreditsDialogProps {
  summary: CreditSummary;
  onClose: () => void;
}

const subtleText = 'text-grey-500 dark:text-muted-foreground';
const panelBorder = 'border border-grey-100 dark:border-border';

const SummaryRow = ({ label, value, isGain = false }: { label: string; value: string; isGain?: boolean }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-grey-600 dark:text-muted-foreground">{label}</dt>
    <dd className={cn('tabular-nums', isGain ? 'text-success-700 dark:text-success-50' : 'text-foreground')}>{value}</dd>
  </div>
);

/**
 * Buys credits from Plans & Billing: a preset pack or another amount, with an optional coupon.
 * Paid on the saved card, or on the checkout page when none is saved. Mounted only while open.
 */
export const BuyCreditsDialog = ({ summary, onClose }: BuyCreditsDialogProps) => {
  const { t } = useTranslation();
  const isDark = useTheme().resolvedTheme === 'dark';
  const presets = presetsOrDefault(summary.presetsChf);
  const [amountChf, setAmountChf] = useState<number | null>(defaultPreset(presets));
  const [coupon, setCoupon] = useState<{ code: string; quote: CouponQuote } | null>(null);
  const payment = useTopUpPayment(summary, amountChf, coupon?.code);
  const { card } = payment;
  const isBusy = payment.isPending || payment.isRedirecting;
  const isLocked = isBusy || !!payment.chargeId;

  const credits = amountChf === null ? null : creditsFor(amountChf, summary.creditsPerChf);
  // What lands in the pool: the coupon's own count when one is applied, since it can add bonus credits.
  const creditsGranted = credits === null ? null : (coupon?.quote.credits ?? credits);
  const bonusCredits =
    coupon && amountChf !== null ? couponBonusCredits(coupon.quote.credits, amountChf, summary.creditsPerChf) : 0;
  const couponEffects = [
    bonusCredits > 0 ? `+${formatNumber(bonusCredits)}` : '',
    coupon && coupon.quote.discountMinor > 0 ? `-${formatMinor(coupon.quote.discountMinor, summary.currency)}` : '',
  ].filter(Boolean);
  const dueMinor = amountChf === null ? null : (coupon?.quote.payableMinor ?? toMinor(amountChf));
  const amountLabel = dueMinor === null ? '' : formatMinor(dueMinor, summary.currency);

  const canPay = amountChf !== null && !isLocked && !payment.isChecking;

  const payLabel = () => {
    const amountText = amountLabel
      ? t('PAY_AMOUNT', { amount: amountLabel, defaultValue: 'Pay {{amount}}' })
      : t('PAY', 'Pay');
    if (!card) return amountText;
    const cardText = cardLabel(card, t('CARD'));
    if (!amountLabel) return t('PAY_WITH_CARD_ONLY', { card: cardText, defaultValue: 'Pay with {{card}}' });
    return t('PAY_WITH_CARD', { amount: amountLabel, card: cardText, defaultValue: 'Pay {{amount}} with {{card}}' });
  };

  const hint = () => {
    if (payment.isRedirecting) return t('OPENING_CHECKOUT', 'Opening checkout...');
    if (card) return t('SAVED_CARD_HINT', 'Your saved card is charged right away.');
    return t('CHECKOUT_HINT', 'You enter your card on the next page, where you can save it for next time.');
  };

  const applyCoupon = (code: string, quote: CouponQuote) => {
    setCoupon({ code, quote });
    // A coupon that covers the whole amount has nothing to charge, so it is redeemed straight away.
    if (quote.payableMinor === 0) payment.start(undefined, code);
  };

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && !isBusy && onClose()}>
      <DialogContent className="!flex max-h-[90dvh] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-dialog border-transparent p-0 shadow-choice-card sm:max-w-[560px]">
        <DialogHeader className="space-y-1 px-5 pb-5 pt-7 sm:px-7">
          <DialogTitle className="text-lg font-medium leading-normal tracking-normal">
            {t('BUY_CREDITS', 'Buy credits')}
          </DialogTitle>
          <DialogDescription className={subtleText}>
            {t('BUY_CREDITS_RATE', {
              amount: formatMinor(100, summary.currency),
              credits: formatNumber(summary.creditsPerChf),
              defaultValue: "{{amount}} = {{credits}} credits. They go into your team's pool and never expire.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-1 [scrollbar-width:thin] [scrollbar-color:hsl(var(--border))_transparent] sm:px-7">
          {summary.openTopUp && <OpenCheckoutNotice openTopUp={summary.openTopUp} />}

          <fieldset disabled={isLocked} className="min-w-0 border-0 p-0">
            <AmountPicker
              variant="dialog"
              label={t('AMOUNT', 'Amount')}
              hint={t('AMOUNT_RANGE_HINT', {
                min: formatMinor(toMinor(summary.minTopUpChf), summary.currency),
                max: formatMinor(toMinor(summary.maxTopUpChf), summary.currency),
                defaultValue: '{{min}} to {{max}}',
              })}
              presets={presets}
              minChf={summary.minTopUpChf}
              maxChf={summary.maxTopUpChf}
              initialChf={amountChf}
              creditsPerChf={summary.creditsPerChf}
              onChange={(amount) => {
                setAmountChf(amount);
                setCoupon(null);
              }}
            />
          </fieldset>

          <CouponRedeem
            triggerStyle="icon"
            amountChf={amountChf}
            creditsPerChf={summary.creditsPerChf}
            disabled={isLocked}
            applied={coupon?.quote}
            onQuote={applyCoupon}
            onClear={() => setCoupon(null)}
          />

          <dl className={cn('rounded-section text-sm', panelBorder)} aria-live="polite">
            <div className="space-y-2 px-4 py-3.5">
              <SummaryRow
                label={t('CREDITS', 'Credits')}
                value={credits === null ? '-' : formatNumber(credits)}
              />
              {coupon && couponEffects.length > 0 && (
                <SummaryRow
                  label={t('COUPON_WITH_CODE', {
                    code: coupon.quote.couponCode || coupon.code,
                    defaultValue: 'Coupon {{code}}',
                  })}
                  value={couponEffects.join(' · ')}
                  isGain
                />
              )}
              <SummaryRow
                label={t('POOL_AFTER_LABEL', 'Pool after')}
                value={creditsGranted === null ? '-' : formatCredits(summary.balanceCredits + creditsGranted)}
              />
            </div>
            <div className="flex justify-between gap-4 border-t border-grey-100 px-4 py-3 font-medium leading-body text-foreground dark:border-border">
              <dt>{t('DUE_NOW', 'Due now')}</dt>
              <dd className="tabular-nums">{amountLabel || '-'}</dd>
            </div>
          </dl>

          {payment.hasFailed && (
            <Alert variant="destructive">
              <AlertDescription>{payment.failureText}</AlertDescription>
            </Alert>
          )}

          {payment.chargeId && (
            <TopUpProgress
              topUpId={payment.chargeId}
              onDone={onClose}
              onUseCheckout={() => {
                payment.clearCharge();
                payment.start();
              }}
            />
          )}
        </div>

        <DialogFooter className="flex-col gap-2 px-5 pb-7 pt-6 sm:flex-col sm:space-x-0 sm:px-7">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <Button
              className={cn('h-10 w-full rounded-full px-5 sm:w-auto', primaryButtonClass(isDark))}
              disabled={!canPay}
              loading={card ? payment.isChargingCard : isBusy}
              onClick={() => payment.pay(card?.paymentMethodId)}
            >
              {!isBusy && <CreditCard aria-hidden="true" />}
              {payLabel()}
            </Button>
            {card && (
              <Button
                variant="ghost"
                className="h-10 w-full rounded-full px-4 text-grey-900 dark:text-foreground sm:w-auto"
                disabled={!canPay}
                loading={isBusy && !payment.isChargingCard}
                onClick={() => payment.pay()}
              >
                {t('USE_DIFFERENT_CARD', 'Use a different card')}
              </Button>
            )}
          </div>
          <p className={cn('flex items-center gap-1.5 text-xs', subtleText)} role="status">
            <Lock aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            {hint()}
          </p>
        </DialogFooter>

        <TopUpBillingDialog
          open={payment.billing.isOpen}
          amount={amountLabel}
          onClose={payment.billing.close}
          onSaved={payment.billing.onSaved}
        />
      </DialogContent>
    </Dialog>
  );
};
